import React from "react";
import { TimeOfDay } from "../../generated/gpb/api/route";
import { IMPACT_LEVEL } from "../UserVisibleStrings";
import { RouteMetricSummary } from "./RouteUtils";

type SegmentMetricProps = {
    routeMetrics: Array<JsonRouteMetric>,
    heading: string,
};

export type JsonRouteMetric = {
    name: string,
    value: number | string,
    unit?: string,
    digits?: number,
};

// Converts duration in ms to hours
export function durationToJson( name: string, durationInMs: number ): JsonRouteMetric
{
    return { name, value: durationInMs / 3600000.0, unit: "hr" };
}

// Converts TimeOfDay object to readable string
export function timeOfDayToJson( name: string, time: TimeOfDay | undefined ): JsonRouteMetric | undefined
{
    if ( time === undefined ) return undefined;

    const hour = time.hour ?? 0;
    const minute = time.minute ?? 0;

    const hourVal = hour % 12 === 0 ? 12 : hour % 12;
    const amPm = hour < 12 ? "AM" : "PM";
    const formattedMinute = minute.toString().padStart( 2, "0" );

    return {
        name,
        value: `${hourVal}:${formattedMinute} ${amPm}`,
    };
}

// Converts RouteMetricSummary into display-friendly array
export function routeMetricToJson( metric: RouteMetricSummary ): Array<JsonRouteMetric>
{
    const result: Array<JsonRouteMetric> = [];
    if ( metric === undefined ) return result;

    result.push( { name: "Distance", value: metric.distanceMi, unit: "mi" } );

    if ( metric.totalDurationMs ) result.push( durationToJson( "Total Duration", metric.totalDurationMs ) );
    if ( metric.drivingDurationMs ) result.push( durationToJson( "Driving Duration", metric.drivingDurationMs ) );
    if ( metric.totalWeatherDelayDurationMs )
    {
        result.push( durationToJson( "Weather Delay Duration", metric.totalWeatherDelayDurationMs ) );
    }
    if ( metric.overnightStayDurationMs )
    {
        result.push( durationToJson( "Overnight Stay Duration", metric.overnightStayDurationMs ) );
    }

    result.push( { name: "Overnights", value: metric.numberOfOvernights, digits: 0 } );

    const impactLevels = [
        { label: "0", value: metric.travelDurationMsInRiskZoneNone },
        { label: "1", value: metric.travelDurationMsInRiskZoneLow },
        { label: "2", value: metric.travelDurationMsInRiskZoneLowMedium },
        { label: "3", value: metric.travelDurationMsInRiskZoneMedium },
        { label: "4", value: metric.travelDurationMsInRiskZoneMediumHigh },
        { label: "5", value: metric.travelDurationMsInRiskZoneHigh },
    ];

    impactLevels.forEach( ( { label, value } ) =>
    {
        if ( value )
        {
            result.push( durationToJson( `Travel Duration ${IMPACT_LEVEL} ${label}`, value ) );
        }
    } );

    return result;
}

// 📦 Main Component (Plain HTML Table)
export default function SegmentMetricTable( { routeMetrics, heading }: Readonly<SegmentMetricProps> )
{
    if ( routeMetrics === undefined || routeMetrics.length === 0 ) return null;

    return (
        <div className = "segment-metric-table">
            <table style = {{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style = {{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc" }}>{heading}</th>
                        <th style = {{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc" }}>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {routeMetrics.map( ( metric, index ) =>
                    {
                        let valueFormatted: string;
                        if ( typeof metric.value === "number" )
                        {
                            const displayValue =
                            metric.value < 0.1 && metric.value > 0 ? "<0.1" : metric.value.toFixed( metric.digits ?? 1 );
                            valueFormatted = `${displayValue} ${metric.unit ?? ""}`;
                        }
                        else
                        {
                            valueFormatted = metric.value + ( metric.unit !== undefined ? ` ${metric.unit}` : "" );
                        }

                        return (
                            <tr key = {index}>
                                <td style = {{ padding: "8px", borderBottom: "1px solid #eee" }}>{metric.name}</td>
                                <td style = {{ padding: "8px", borderBottom: "1px solid #eee" }}>{valueFormatted}</td>
                            </tr>
                        );
                    } )}
                </tbody>
            </table>
        </div>
    );
}
